import React from "react";
import { KTMScreen, ActionEvent, RenderEvent, renderScreen } from "ktm-ui";
import { KTMApi } from "api/KTMApi";
import { Layout, Dialog, Button, Display, Line, Card } from "ktm-ui";
import TaxLookup from "enroll/lookups/Tax_jurisdiction";
import CustomToolTip from "../../components/CustomToolTip";
import { CustomDropdown } from "components/CustomDropdown";

interface IdsPolicy {
  Id: string;
  Name: string;
  PolicyNo: string;
  EnrollmentNote: string;
  DependentAge: number;
  StudentAge: number;
  TaxJurisdiction_Id: string;
  Type: string;
  StartDate: string;
  UpdatedAt: string;
  UpdatedBy: string;
  Language: string;
  ContestCode: string;
}

interface IdsTaxJurisdiction {
  Id: string;
  Name: string;
}

const languages = [
  { key: "English", value: "ENG" },
  { key: "French",  value: "FR"  },
];

export default KTMScreen(
  { name: "enroll.policy.edit-details" },
  class extends React.Component<{ policy_id: string }> {
    data: {
      dsPolicy: IdsPolicy;
      dsTaxJurisdiction: IdsTaxJurisdiction;
    };

    // Store original data for comparison
    originalData: IdsPolicy | null = null;

    state = {
      selectedLang: languages[0].value,
    };

    render() {
      return renderScreen(this);
    }

    async start({ screen }: ActionEvent) {
      screen.defaultRecord = "@dsPolicy";
      await KTMApi.start(screen, {
        "@policy_id": this.props.policy_id,
      });
      screen.title = "Edit policy details";
      // Store a copy of the original data
      this.originalData = { ...this.data.dsPolicy };
    }

    tax_lookup({ getValue, eventValue }: RenderEvent) {
      return <TaxLookup eventValue={eventValue} />;
    }

    main({ field, draw, action }: RenderEvent) {
      return (
        <Layout>
          {draw(this.header)}
          {draw(this.detail_card)}
          {draw(this.footer)}
        </Layout>
      );
    }

    header({ field, draw, action }: RenderEvent) {
      return <Dialog.Header label="Edit Policy Details"></Dialog.Header>;
    }

    footer({ field, draw, action }: RenderEvent) {
      var isDisabled =
        this.data.dsPolicy.Type === "-- Select --" ||
        this.data.dsPolicy.StudentAge === 0 ||
        this.data.dsPolicy.DependentAge === 0 ||
        this.data.dsPolicy.TaxJurisdiction_Id === null
          ? true
          : false;
      return (
        <Dialog.Footer>
          <Button
            action={action(this.submit)}
            label="Save"
            size="large"
            style="primary"
            disable={isDisabled}
          />
        </Dialog.Footer>
      );
    }

    detail_card({ field, draw, action }: RenderEvent) {
      return (
        <Card>
          <div className="policy-edit-card">
          <Display
            required
            field={field("Type")}
            size="medium"
            textStyle="bold"
            toolTip={
              <CustomToolTip
                header="Type (options are Group or Individual) "
                bodyContent="Marketing only field. Group accounts have employees.
                             An individual account is a Business Owner only account."
              />
            }
          />
          <Line>
            <Display
              required
              field={field("DependentAge")}
              toolTip={
                <CustomToolTip
                  header="Child Age"
                  bodyContent="Typically 20, meaning coverage ends at 21."
                />
              }
            />
            <Display
              required
              field={field("StudentAge")}
              toolTip={
                <CustomToolTip
                  header="Student Age"
                  bodyContent="Typically 24, meaning coverage ends at 25."
                />
              }
            />
          </Line>
          <Line>
            <Display
              required
              field={field("TaxJurisdiction_Id")}
              toolTip={
                <CustomToolTip
                  header="Province"
                  bodyContent="Each claim is taxed based on the home address of the employee."
                />
              }
            />
          </Line>
          <Line>
            <div className="RT-TextBox">
              <label>Language</label>
              <CustomDropdown
                options={languages.map(l => l.key)}
                selectedOption={
                  languages.find(l => l.value === (this.data?.dsPolicy?.Language ?? this.state.selectedLang))?.key
                    ?? "-- Select --"
                }
                setSelectedOption={this.setSelectedLanguageOption}
              />
            </div>
            <div className="RT-TextBox">
              <label>Contest Code</label>
              <div className="RT-TextBox__container">
                <input
                  className="RT-TextBox__input"
                  type="text"
                  maxLength={32}
                  value={this.data?.dsPolicy?.ContestCode ?? ""}
                  onChange={(e) => this.setContestCode(e.target.value)}
                />
              </div>
            </div>
          </Line>
          </div>
        </Card>
      );
    }

    setSelectedLanguageOption = async (selectedLabel: string) => {
      const match = languages.find(l => l.key === selectedLabel);
      const code = match?.value ?? "";

      this.setState({ selectedLang: code });
      if (this.data?.dsPolicy) {
        this.data.dsPolicy.Language = code;
      }
    };

    normalizeContestCode = (raw: string | null | undefined): string => {
      if (!raw) return "";
      return raw.replace(/\s+/g, "").slice(0, 32);
    };

    setContestCode = (raw: string) => {
      const normalized = this.normalizeContestCode(raw);
      if (this.data?.dsPolicy) {
        this.data.dsPolicy.ContestCode = normalized;
        this.forceUpdate();
      }
    };

    getChangedFields(originalData, updatedData) {
      if (!originalData) return {};

      const changes = {};
      for (const key in updatedData) {
        if (updatedData[key] !== originalData[key]) {
          changes[key] = {
            oldValue:
              originalData[key] !== undefined ? originalData[key] : "N/A",
            newValue: updatedData[key] !== undefined ? updatedData[key] : "N/A",
          };
        }
      }
      return changes;
    }

    async submit({ screen }: ActionEvent) {
      const cc = this.normalizeContestCode(this.data.dsPolicy.ContestCode);
      this.data.dsPolicy.ContestCode = cc === "" ? null : cc;

      // Track changes
      const changes = this.getChangedFields(
        this.originalData,
        this.data.dsPolicy
      );

      const logDetails = Object.entries(changes)
        .map(([key, value]) => {
          const change = value as { oldValue: any; newValue: any };
          return `${key}: ${change.oldValue} → ${change.newValue}`;
        })
        .join(" | ");

      const policyLogs = {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          Note: logDetails,
          PolicyId: this.props.policy_id,
          Action: "Policy Update Policy details",
        }),
      };

      await KTMApi.action(screen, "submit", {
        "@dsPolicy.Name": this.data.dsPolicy.Name,
        "@dsPolicy.DependentAge": this.data.dsPolicy.DependentAge,
        "@dsPolicy.StudentAge": this.data.dsPolicy.StudentAge,
        "@dsPolicy.TaxJurisdiction_Id": this.data.dsPolicy.TaxJurisdiction_Id,
        "@dsPolicy.Type": this.data.dsPolicy.Type,
        "@dsPolicy.Language": this.data.dsPolicy.Language,
        "@dsPolicy.ContestCode": this.data.dsPolicy.ContestCode,
        "@policy_id": this.props.policy_id,
      }).then(() => {
        fetch("/api/PlanAdmin/updatepolicylogs", policyLogs);
      });
      screen.recordChanged("updated", this.props.policy_id);
      screen.close({ continue: true });
    }
  }
);
